import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@ui/lib/utils";

// YANGO DELI status/count badge (BF-578, re-skinned BF-733). Rounded corners
// (--radius, rounded-md) — still deliberately distinct from the fully pill-shaped
// `chip` (the system's single pill exception). No shadow tier. Public variant API
// preserved for existing consumers; colour resolves through the retuned tokens
// (brand-yellow fill / black label for the default variant).
const badgeVariants = cva(
  "inline-flex items-center justify-center rounded-md border px-2 py-0.5 type-heading-sm w-fit whitespace-nowrap shrink-0 [&>svg]:size-3 gap-1 [&>svg]:pointer-events-none outline-ring/50 focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none transition-[color,background-color]",
  {
    variants: {
      variant: {
        default: "border-transparent bg-primary text-primary-foreground [a&]:hover:bg-primary/90",
        secondary:
          "border-transparent bg-secondary text-secondary-foreground [a&]:hover:bg-secondary/80",
        // functional status colour (semantic hue, not brand yellow).
        destructive:
          "border-transparent bg-destructive text-destructive-foreground [a&]:hover:bg-destructive/90",
        outline:
          "border-border text-foreground [a&]:hover:bg-accent [a&]:hover:text-accent-foreground",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
);

function Badge({
  className,
  variant,
  asChild = false,
  ...props
}: React.ComponentProps<"span"> & VariantProps<typeof badgeVariants> & { asChild?: boolean }) {
  const Comp = asChild ? Slot : "span";

  return (
    <Comp data-slot="badge" className={cn(badgeVariants({ variant }), className)} {...props} />
  );
}

export { Badge, badgeVariants };
